import { onScopeDispose, ref, watchEffect, type Ref } from 'vue';
import { backoffFor, isIdle } from '@/api/polling';

/*
 * Keeps a game screen current without a page reload.
 *
 * A composable rather than logic in the view, so the timer is testable on fake time without
 * mounting anything — and so both game screens share one set of rules about when *not* to ask.
 * The policy itself (how often, and when to stop) lives in api/polling.ts;
 * docs/decisions/0009-polling-not-websockets.md says why this is a timer at all.
 */

export interface UseGamePollingOptions {
  /** Re-reads the game. Resolves false when the read failed, which is what backs the timer off. */
  tick: () => Promise<boolean>;
  /** Milliseconds between reads, or null to stop. Re-evaluated after every tick. */
  interval: () => number | null;
  /** True while the screen has a mutation of its own in flight. */
  busy?: () => boolean;
}

export interface UseGamePolling {
  /** True while a read is in flight, whether the timer or the button asked for it. */
  refreshing: Ref<boolean>;
  /** Consecutive failed reads. Non-zero means the screen may be showing something stale. */
  failures: Ref<number>;
  /** Reading has stopped because nobody has touched the page for an hour. */
  idle: Ref<boolean>;
  refreshNow: () => Promise<void>;
}

/** Unambiguous intent, and cheap: both only store a timestamp. Scrolling can be programmatic. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const;

export function useGamePolling(options: UseGamePollingOptions): UseGamePolling {
  const refreshing = ref(false);
  const failures = ref(0);
  const idle = ref(false);

  let timer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  let lastActivityAt = Date.now();

  /**
   * Any deliberate interaction keeps the tab alive. Deliberately does *not* clear `idle` once it
   * is set: the banner asks for the Refresh button specifically, so a stray tap cannot swap the
   * board out from under somebody who is mid-tap.
   */
  const onActivity = (): void => {
    if (!idle.value) lastActivityAt = Date.now();
  };

  const clear = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };

  const hidden = (): boolean =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden';

  async function read(): Promise<void> {
    // One in flight at a time. A slow read must not stack up behind itself on a bad connection.
    if (refreshing.value) return;
    if (idle.value) return;

    refreshing.value = true;
    try {
      failures.value = (await options.tick()) ? 0 : failures.value + 1;
    } finally {
      refreshing.value = false;
    }
  }

  /**
   * setTimeout re-armed after each read rather than setInterval: the delay depends on the game's
   * current status and on how many reads have just failed, and neither is known in advance.
   */
  function schedule(): void {
    clear();
    if (disposed) return;

    const base = options.interval();
    // Null means this game cannot change again, or there is no game. Arm nothing.
    if (base === null) return;
    // A hidden tab is armed by the visibility listener instead, not by a timer nobody will see.
    if (hidden()) return;
    // Nobody is reading this. Only the button starts it again.
    if (idle.value) return;

    timer = setTimeout(
      () => {
        void (async () => {
          // Checked here rather than on a timer of its own: the banner appears within one interval
          // of the hour, which is close enough for something that has been still for an hour.
          if (isIdle(Date.now() - lastActivityAt)) {
            idle.value = true;
            return;
          }

          if (!(options.busy?.() ?? false)) await read();
          schedule();
        })();
      },
      backoffFor(base, failures.value),
    );
  }

  function onVisibilityChange(): void {
    if (hidden()) {
      clear();
      return;
    }
    // Coming back to an idle tab does not resume it: the banner names the button, and the button
    // is the only thing that clears it.
    if (idle.value) return;

    // Read at once rather than after another full interval: somebody just came back to this tab,
    // and being current the moment they look at it is the whole point.
    void (async () => {
      if (!(options.busy?.() ?? false)) await read();
      schedule();
    })();
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, onActivity, { passive: true });
    }
  }

  // watchEffect, so a status change — the host reopening a locked game — re-arms a timer that
  // had stopped, without the view having to notice.
  watchEffect(() => {
    options.interval();
    schedule();
  });

  onScopeDispose(() => {
    disposed = true;
    clear();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity);
      }
    }
  });

  return {
    refreshing,
    failures,
    idle,
    /**
     * The manual escape hatch, and the only way out of the idle state. Reads now and re-arms,
     * whatever the backoff had decided.
     */
    refreshNow: async () => {
      lastActivityAt = Date.now();
      idle.value = false;
      await read();
      schedule();
    },
  };
}
